import { StorageService } from '@/services/storage';

const CHANNEL_NAME = 'snowball-idv';

interface IDVStatusMessage {
  type: 'idv-completed';
  continuePath: string;
  timestamp: number;
}

/** Another tab wrote the outbox; the stored outbox is the one to show. */
interface OutboxChangedMessage {
  type: 'outbox-changed';
  timestamp: number;
}

class CrossTabCommunication {
  private channel: BroadcastChannel;
  private listeners: Set<(message: IDVStatusMessage) => void> = new Set();
  private outboxListeners: Set<() => void> = new Set();

  constructor() {
    this.channel = new BroadcastChannel(CHANNEL_NAME);
    this.channel.onmessage = (event: MessageEvent) => {
      if (event.data?.type === 'idv-completed') {
        this.notifyListeners(event.data);
      } else if (event.data?.type === 'outbox-changed') {
        this.outboxListeners.forEach(listener => {
          try {
            listener();
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error in cross-tab outbox listener:', error);
          }
        });
      }
    };
  }

  broadcastOutboxChange(): void {
    const message: OutboxChangedMessage = {
      type: 'outbox-changed',
      timestamp: Date.now(),
    };
    this.channel.postMessage(message);
  }

  onOutboxChange(callback: () => void): () => void {
    this.outboxListeners.add(callback);
    return () => {
      this.outboxListeners.delete(callback);
    };
  }

  broadcastIDVCompletion(continuePath: string): void {
    const message: IDVStatusMessage = {
      type: 'idv-completed',
      continuePath,
      timestamp: Date.now(),
    };
    this.channel.postMessage(message);
  }

  onIDVCompletion(callback: (message: IDVStatusMessage) => void): () => void {
    this.listeners.add(callback);
    return () => {
      this.listeners.delete(callback);
    };
  }

  private notifyListeners(message: IDVStatusMessage): void {
    this.listeners.forEach(listener => {
      try {
        listener(message);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in cross-tab listener:', error);
      }
    });
  }

  destroy(): void {
    this.channel.close();
    this.listeners.clear();
    this.outboxListeners.clear();
  }
}

let instance: CrossTabCommunication | null = null;

export function getCrossTabCommunication(): CrossTabCommunication {
  if (!instance) {
    instance = new CrossTabCommunication();
  }
  return instance;
}

export async function hasOtherTabs(): Promise<boolean> {
  if (!window.name || !window.name.startsWith('snowball-tab-')) {
    window.name = `snowball-tab-${Date.now()}-${Math.random()}`;
  }

  const tabIds = new Set<string>(await StorageService.getTabIds());

  tabIds.add(window.name);

  // Clean up tab IDs older than 5 minutes
  const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
  const cleanedIds = Array.from(tabIds).filter(id => {
    const timestamp = parseInt(id.split('-')[2] || '0', 10);
    return timestamp > fiveMinutesAgo;
  });

  await StorageService.setTabIds(cleanedIds);

  return cleanedIds.length > 1;
}

export async function cleanupTabId(): Promise<void> {
  try {
    const tabIds = new Set<string>(await StorageService.getTabIds());
    tabIds.delete(window.name);
    await StorageService.setTabIds(Array.from(tabIds));
  } catch {
    // Ignore errors
  }
}
